import type {
  IAPMAppDataService,
  InFieldUser,
  Template,
  TemplateItem,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { Filters, Sort } from '@cognite/fdm-client';
import { parseFilters } from '@cognite/fdm-client/src/utils/parse-filters';
import type { CogniteClient, DirectRelationReference } from '@cognite/sdk';

import { gql } from '../../utils/graphql-request';
import {
  getSourceCreatedFields,
  getSourceUpdatedFields,
} from '../shared-utils/sourceable-utils';

import {
  isMappableCdmTemplateResponse,
  mapTemplateItemToUpsertType,
  mapTemplateResponseToTemplate,
  mapTemplateToUpsertType,
} from './cdm-template-service-utils';
import type {
  CdmTemplateItemResponseType,
  CdmTemplateResponseType,
} from './cdm-types';
import type { ITemplateService } from './template-service-interface';

export type APMClientDependencies = {
  templates: Pick<
    IAPMAppDataService<CdmTemplateResponseType>,
    'list' | 'upsert' | 'delete'
  >;
  templateItems: Pick<
    IAPMAppDataService<CdmTemplateItemResponseType>,
    'upsert' | 'delete'
  >;
  userInstanceSpace: string;
  appDataModelSpace: string;
  appDataModelName: string;
  appDataModelVersion: string;
  appDataInstanceSpace: string;
  supportsSolutionTags: () => boolean;
};

type CogniteClientInstancesDependencies = Pick<
  CogniteClient['instances'],
  'search'
>;
type CogniteClientViewsDependencies = Pick<CogniteClient['views'], 'retrieve'>;

export type CogniteClientDependencies = Pick<
  CogniteClient,
  'post' | 'getBaseUrl' | 'project'
> & {
  instances: CogniteClientInstancesDependencies;
  views: CogniteClientViewsDependencies;
};

export class CdmTemplateService extends FDMClient implements ITemplateService {
  apmClient: APMClientDependencies;
  modelSpace: string;
  modelName: string;
  modelVersion: string;
  instanceSpace: string;
  rootLocation?: DirectRelationReference;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies,
    rootLocation?: DirectRelationReference
  ) {
    super(client);
    this.apmClient = apmClient;
    this.modelSpace = apmClient.appDataModelSpace;
    this.modelName = apmClient.appDataModelName;
    this.modelVersion = apmClient.appDataModelVersion;
    this.instanceSpace = apmClient.appDataInstanceSpace;
    this.rootLocation = rootLocation;
  }

  async getTemplates(
    filters?: Filters,
    sort?: Sort,
    pageSize = 1000,
    showArchived = false
  ): Promise<Template[]> {
    const {
      list: { items: templates },
    }: {
      list: {
        items: CdmTemplateResponseType[];
      };
    } = await this.apmClient.templates.list(
      `
      externalId
      space
      title
      status
      assignedTo
      sourceCreatedUser
      sourceUpdatedUser
      sourceCreatedTime
      sourceUpdatedTime
      isArchived
      solutionTags {
          items {
            externalId
            name
          }
        }
      `,
      { filters, sort, pageSize },
      showArchived
    );
    return templates
      .filter(isMappableCdmTemplateResponse)
      .map(mapTemplateResponseToTemplate);
  }

  async getTemplateData(
    templateFilter?: Filters,
    templateSort?: Sort,
    itemsFilter?: Filters,
    _itemsSort?: Sort,
    pageSize = 1000
  ): Promise<Template> {
    const {
      listTemplate: {
        items: [cdmTemplate],
      },
    } = await super.graphQL<{
      listTemplate: {
        items: CdmTemplateResponseType[];
      };
    }>(
      gql`
        query GetTemplateData(
          $templateFilter: _ListTemplateFilter
          $templateSort: [_TemplateSort!]
          $itemsFilter: _ListTemplateItemFilter
          $pageSize: Int
        ) {
          listTemplate(filter: $templateFilter, sort: $templateSort) {
            items {
              externalId
              space
              title
              status
              assignedTo
              isArchived
              sourceCreatedUser
              sourceUpdatedUser
              sourceCreatedTime
              sourceUpdatedTime
              solutionTags {
                items {
                  externalId
                  name
                }
              }
              schedules {
                items {
                  sourceCreatedTime
                  externalId
                  status
                  startTime
                  endTime
                  timezone
                  freq
                  interval
                  until
                  byDay
                  byMonth
                  template {
                    externalId
                    space
                  }
                  templateItems(filter: $itemsFilter, first: $pageSize) {
                    items {
                      externalId
                      space
                    }
                  }
                }
              }
              templateItems(filter: $itemsFilter, first: $pageSize) {
                items {
                  externalId
                  space
                  title
                  description
                  order
                  labels
                  sourceCreatedUser
                  sourceUpdatedUser
                  sourceCreatedTime
                  sourceUpdatedTime
                  asset {
                    externalId
                    title
                    description
                    space
                  }
                  measurements {
                    items {
                      type
                      externalId
                      title
                      description
                      timeseries {
                        externalId
                        space
                        name
                        description
                        sourceUnit
                        assets {
                          items {
                            externalId
                            space
                          }
                        }
                      }
                      measuredAt
                      min
                      max
                      numericReading
                      stringReading
                      order
                      options
                    }
                  }
                }
              }
            }
          }
        }
      `,
      this.modelSpace,
      this.modelName,
      this.modelVersion,
      {
        templateFilter: parseFilters({
          and: [
            { ...templateFilter },
            {
              equals: {
                property: 'space',
                eq: this.instanceSpace,
              },
            },
          ],
        }),
        templateSort,
        itemsFilter: parseFilters({
          and: [
            { ...itemsFilter },
            {
              not: {
                equals: {
                  property: 'isArchived',
                  eq: true,
                },
              },
            },
          ],
        }),
        pageSize,
      }
    );

    return mapTemplateResponseToTemplate(cdmTemplate);
  }

  async createTemplate(newTemplate: Template, user: InFieldUser) {
    const template: Template = {
      ...newTemplate,
      ...getSourceCreatedFields(user),
      rootLocation: newTemplate.rootLocation ?? this.rootLocation,
    };

    return this.upsertTemplates([template]);
  }

  async updateTemplate(newTemplate: Template, user: InFieldUser) {
    const template: Template = {
      ...newTemplate,
      ...getSourceUpdatedFields(user),
    };

    return this.upsertTemplates([template]);
  }

  async upsertTemplates(newTemplates: Template[]) {
    if (!this.rootLocation) {
      throw new Error('Missing root location for template service');
    }
    const { rootLocation } = this;
    const templates = newTemplates.map((template) =>
      mapTemplateToUpsertType(template, rootLocation, this.instanceSpace)
    );

    return this.apmClient.templates.upsert(templates);
  }

  async createTemplateItems(templateItems: TemplateItem[], user: InFieldUser) {
    const upsertedTemplateItems: TemplateItem[] = templateItems.map(
      (templateItem) => ({
        ...templateItem,
        ...getSourceCreatedFields(user),
      })
    );

    return this.upsertTemplateItems(upsertedTemplateItems);
  }

  async updateTemplateItems(templateItems: TemplateItem[], user: InFieldUser) {
    const upsertedTemplateItems: TemplateItem[] = templateItems.map(
      (templateItem) => ({
        ...templateItem,
        ...getSourceUpdatedFields(user),
      })
    );

    return this.upsertTemplateItems(upsertedTemplateItems);
  }

  async upsertTemplateItems(templateItems: TemplateItem[]) {
    const upsertedTemplateItems = templateItems.map(
      mapTemplateItemToUpsertType
    );

    return this.apmClient.templateItems.upsert(upsertedTemplateItems);
  }

  async softDeleteTemplateItems(externalIds: string[]) {
    const templateItems = externalIds.map((externalId) => ({
      externalId,
      isArchived: true,
    }));
    await this.apmClient.templateItems.upsert(templateItems);
    return externalIds;
  }

  async deleteTemplateItem(externalIds: string[]) {
    return this.apmClient.templateItems.delete(externalIds);
  }

  async deleteTemplate(externalIds: string[]) {
    return this.apmClient.templates.delete(externalIds);
  }
}
export const createCdmTemplateService = (
  ...args: ConstructorParameters<typeof CdmTemplateService>
): ITemplateService => new CdmTemplateService(...args);
